import { useState, useMemo, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import {
  TrendingDown,
  TrendingUp,
  BarChart3,
  LineChart,
  Info,
  Lightbulb,
  Target,
  Sun,
  Cloud,
  Snowflake,
  Flower2,
  ArrowLeftRight,
  Plus,
  X,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { Badge } from "../components/ui/Badge";
import { Spinner } from "../components/ui/Spinner";
import { useRequireAuth } from "../hooks/useAuth";
import { useSeasonalityStore } from "../stores/seasonalityStore";
import { ArriviPresenzeSection } from "../components/seasonality/ArriviPresenzeSection";
import { ComparisonChart } from "../components/seasonality/ComparisonChart";
import { Autocomplete } from "../components/ui/Autocomplete";
import {
  useAvailableYears,
  useIstatPresenze,
  useIstatIndicators,
  useAllIstatIndicators,
  useIstatCapacity,
} from "../hooks/useIstatData";
import { cn } from "../lib/utils";
import type { SeasonalityIndicator, IstatPresenzeMensili } from "../types/database";

const monthNames = [
  "Gen", "Feb", "Mar", "Apr", "Mag", "Giu",
  "Lug", "Ago", "Set", "Ott", "Nov", "Dic"
];

const monthNamesFull = [
  "Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno",
  "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"
];

const RESIDENZE = [
  { value: "mondo", label: "Totale mondo" },
  { value: "italia", label: "Italia" },
  { value: "esteri", label: "Paesi esteri" },
];

const TIPOLOGIE = [
  { value: "totale", label: "Totale esercizi" },
  { value: "alberghiero", label: "Esercizi alberghieri" },
  { value: "extra-alberghiero", label: "Esercizi extra-alberghieri" },
];

const COMPARISON_COLORS = ["#e07b39", "#8b5cf6", "#06b6d4", "#f43f5e"];

const seasonIcons = {
  winter: Snowflake,
  spring: Flower2,
  summer: Sun,
  autumn: Cloud,
};

function getSeasonFromMonth(month: number): keyof typeof seasonIcons {
  if (month >= 3 && month <= 5) return "spring";
  if (month >= 6 && month <= 8) return "summer";
  if (month >= 9 && month <= 11) return "autumn";
  return "winter";
}

function formatNum(n: number | null | undefined): string {
  if (n === null || n === undefined) return "-";
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + "M";
  if (n >= 1_000) return (n / 1_000).toFixed(0) + "K";
  return n.toFixed(1);
}

export function Seasonality() {
  const { isLoading: authLoading } = useRequireAuth();

  // Store state
  const {
    activeTab,
    setActiveTab,
    chartView,
    setChartView,
    istatTerritorio,
    setIstatTerritorio,
    istatAnno,
    setIstatAnno,
    istatAnnoCompare,
    setIstatAnnoCompare,
    istatResidenza,
    setIstatResidenza,
    istatTipologia,
    setIstatTipologia,
    comparisonTerritories,
    setComparisonTerritories,
  } = useSeasonalityStore();

  // Local state
  const [showComparison, setShowComparison] = useState(false);

  // Available years per tab (from DB)
  const { data: availableYears } = useAvailableYears();
  const currentTabYears = useMemo(() => {
    if (!availableYears) return [];
    return activeTab === "arrivi" ? availableYears.arrivi : availableYears.presenze;
  }, [availableYears, activeTab]);

  const yearOptions = useMemo(() =>
    currentTabYears.map(y => ({ value: String(y), label: String(y) })),
    [currentTabYears]
  );

  // Auto-select first available year, reset on tab change if invalid
  const effectiveAnno = (currentTabYears.includes(istatAnno) ? istatAnno : currentTabYears[0]) || 0;
  useEffect(() => {
    if (currentTabYears.length > 0 && !currentTabYears.includes(istatAnno)) {
      setIstatAnno(currentTabYears[0]);
    }
  }, [currentTabYears, istatAnno, setIstatAnno]);

  // Clear comparison year if it becomes invalid
  const effectiveAnnoCompare = istatAnnoCompare && currentTabYears.includes(istatAnnoCompare) ? istatAnnoCompare : null;
  useEffect(() => {
    if (istatAnnoCompare && !currentTabYears.includes(istatAnnoCompare)) {
      setIstatAnnoCompare(null);
    }
  }, [currentTabYears, istatAnnoCompare, setIstatAnnoCompare]);

  // Year comparison options (exclude primary year)
  const yearCompareOptions = useMemo(() =>
    currentTabYears.filter(y => y !== effectiveAnno).map(y => ({ value: String(y), label: String(y) })),
    [currentTabYears, effectiveAnno]
  );

  // ISTAT data queries — only run when we have a valid year
  const { data: presenze = [], isLoading: presenzeLoading } = useIstatPresenze(
    istatTerritorio || undefined, effectiveAnno || undefined, istatResidenza || undefined, istatTipologia || undefined
  );
  const { data: presenzeCompare = [] } = useIstatPresenze(
    effectiveAnnoCompare ? (istatTerritorio || undefined) : undefined,
    effectiveAnnoCompare || undefined,
    istatResidenza || undefined,
    istatTipologia || undefined,
  );
  const { data: indicators = [] } = useIstatIndicators(
    istatTerritorio || undefined, effectiveAnno || undefined, istatResidenza || undefined, istatTipologia || undefined
  );
  const { data: indicatorsCompare = [] } = useIstatIndicators(
    effectiveAnnoCompare ? (istatTerritorio || undefined) : undefined,
    effectiveAnnoCompare || undefined,
    istatResidenza || undefined,
    istatTipologia || undefined,
  );
  const { data: allIndicators = [] } = useAllIstatIndicators(
    effectiveAnno || undefined, istatResidenza || undefined, istatTipologia || undefined
  );
  const { data: capacityData = [] } = useIstatCapacity(
    istatTerritorio || undefined, effectiveAnno || undefined
  );

  // Comparison territories data (up to 4)
  const { data: compTerrPresenze0 = [] } = useIstatPresenze(
    comparisonTerritories[0] || undefined, effectiveAnno || undefined, istatResidenza || undefined, istatTipologia || undefined
  );
  const { data: compTerrPresenze1 = [] } = useIstatPresenze(
    comparisonTerritories[1] || undefined, effectiveAnno || undefined, istatResidenza || undefined, istatTipologia || undefined
  );
  const { data: compTerrPresenze2 = [] } = useIstatPresenze(
    comparisonTerritories[2] || undefined, effectiveAnno || undefined, istatResidenza || undefined, istatTipologia || undefined
  );
  const { data: compTerrPresenze3 = [] } = useIstatPresenze(
    comparisonTerritories[3] || undefined, effectiveAnno || undefined, istatResidenza || undefined, istatTipologia || undefined
  );
  const compTerrPresenzeAll = [compTerrPresenze0, compTerrPresenze1, compTerrPresenze2, compTerrPresenze3];

  // Derived data
  const currentIndicator = indicators.length > 0 ? indicators[0] : null;
  const compareIndicator = indicatorsCompare.length > 0 ? indicatorsCompare[0] : null;
  const currentCapacity = capacityData.length > 0 ? capacityData[0] : null;

  // Territory list: escludiamo l'aggregato nazionale (Italia) + le 20 regioni
  // (richiesta cliente: il modulo Stagionalità lavora a livello provincia).
  // Le Provincie Autonome Bolzano/Trento RESTANO perché sono livello provincia,
  // anche se in Italia sostituiscono la regione Trentino-Alto Adige.
  const EXCLUDED_TERRITORIES = new Set([
    "Italia",
    "Abruzzo", "Basilicata", "Calabria", "Campania", "Emilia-Romagna",
    "Friuli-Venezia Giulia", "Lazio", "Liguria", "Lombardia", "Marche",
    "Molise", "Piemonte", "Puglia", "Sardegna", "Sicilia", "Toscana",
    "Trentino-Alto Adige", "Umbria", "Valle d'Aosta", "Veneto",
  ]);
  const territories = useMemo((): string[] => {
    const set = new Set(allIndicators.map((i: SeasonalityIndicator) => i.territorio));
    EXCLUDED_TERRITORIES.forEach(t => set.delete(t));
    return Array.from(set).sort() as string[];
  }, [allIndicators]);

  // Autocomplete options
  const territoryOptions = useMemo(() =>
    [{ value: "Italia", label: "Italia" }, ...territories.map(t => ({ value: t, label: t }))],
    [territories]
  );
  const comparisonOptions = useMemo(() =>
    ["Italia", ...territories].filter(t => t !== istatTerritorio && !comparisonTerritories.includes(t)).map(t => ({ value: t, label: t })),
    [territories, istatTerritorio, comparisonTerritories]
  );

  // Helper to build monthly array from presenze
  function buildMonthly(data: IstatPresenzeMensili[]) {
    const monthly = Array.from({ length: 12 }, (_, i) => {
      const found = data.find((p: IstatPresenzeMensili) => p.mese === i + 1);
      return found?.presenze || 0;
    });
    const max = Math.max(...monthly, 1);
    const mean = monthly.reduce((s, v) => s + v, 0) / 12;
    const indexed = monthly.map((v, i) => ({ v, i }));
    const sorted = [...indexed].sort((a, b) => b.v - a.v);
    const top3 = new Set(sorted.slice(0, 3).map(x => x.i));
    const bottom3 = new Set(sorted.filter(x => x.v > 0).slice(-3).map(x => x.i));
    return { monthly, max, mean, top3, bottom3 };
  }

  // Monthly chart data
  const chartData = useMemo(() => {
    if (presenze.length === 0) return null;
    return buildMonthly(presenze);
  }, [presenze]);

  const chartDataCompare = useMemo(() => {
    if (presenzeCompare.length === 0) return null;
    return buildMonthly(presenzeCompare);
  }, [presenzeCompare]);

  // Monthly occupancy = P_mese / (PL_giorno × giorni_mese) × 100
  // Disponibile solo se la provincia ha capacità ricettiva nota.
  const PARENT_DAYS_IN_MONTH = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  const occupancyPct = useMemo(() => {
    const pl = currentCapacity?.pl_prov;
    if (!pl || pl <= 0 || !chartData) return null;
    return chartData.monthly.map((p, i) => (p / (pl * PARENT_DAYS_IN_MONTH[i])) * 100);
  }, [chartData, currentCapacity]);

  // Heatmap: all territories, monthly presenze
  const territoryHeatmap = useMemo(() => {
    if (allIndicators.length === 0) return {};
    const grouped: Record<string, { mese_picco: number; sr: number | null; p_totale: number }> = {};
    allIndicators.forEach((ind: SeasonalityIndicator) => {
      grouped[ind.territorio] = { mese_picco: ind.mese_picco, sr: ind.sr, p_totale: ind.p_totale };
    });
    return grouped;
  }, [allIndicators]);

  if (authLoading) {
    return (
      <div className="min-h-[calc(100vh-64px)] flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-64px)] p-4 lg:p-6">
      <div className="max-w-7xl mx-auto space-y-3">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col sm:flex-row sm:items-center justify-between gap-2"
        >
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <TrendingDown className="h-6 w-6 text-[#284899]" />
              Strumenti per destagionalizzare
              <IndicatorTooltip text={"Questo modulo analizza come si distribuiscono nel corso dell'anno le presenze turistiche, sia nazionali che internazionali, nelle strutture alberghiere ed extra-alberghiere, con riferimento ai diversi territori. L'obiettivo è capire in quali periodi si concentrano i flussi e in quali, invece, risultano più deboli, mettendo in evidenza i mesi di picco e quelli di minore attività anche rispetto agli anni precedenti o ad altri territori.\n\nIl modulo aiuta a leggere in modo chiaro la stagionalità della destinazione e a individuare quando intervenire per rafforzare l'offerta nei periodi meno frequentati o gestire meglio quelli di maggiore affluenza, contribuendo così a una distribuzione più equilibrata dei flussi nel tempo."} />
            </h1>
            <p className="text-muted-foreground text-sm">
              Analizza i periodi a bassa domanda e ricevi suggerimenti per destagionalizzare
            </p>
          </div>
        </motion.div>

        {/* Tab Navigation */}
        <div className="flex border-b border-border">
          {[
            { id: "stagionalita" as const, label: "Stagionalita" },
            { id: "arrivi" as const, label: "Arrivi e Presenze" },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                "px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px",
                activeTab === tab.id
                  ? "border-[#284899] text-[#284899]"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab: Arrivi e Presenze */}
        {activeTab === "arrivi" && (
          <ArriviPresenzeSection
            anno={effectiveAnno}
            yearOptions={yearOptions}
            onAnnoChange={(val) => setIstatAnno(val)}
            initialProvincia={istatTerritorio}
          />
        )}

        {/* Tab: Stagionalita (unified) */}
        {activeTab !== "arrivi" && (
          <>
            {/* Filters */}
            <div className="flex flex-wrap gap-3 items-center">
              <Autocomplete
                options={territoryOptions}
                value={istatTerritorio || "Italia"}
                onChange={(val) => setIstatTerritorio(val || "Italia")}
                placeholder="Seleziona territorio..."
                className="min-w-[220px]"
                clearable={istatTerritorio !== "Italia"}
              />

              {yearOptions.length > 0 && (
                <div className="flex items-center gap-1.5">
                  <Autocomplete
                    options={yearOptions}
                    value={effectiveAnno ? String(effectiveAnno) : ""}
                    onChange={(val) => setIstatAnno(Number(val) || effectiveAnno)}
                    placeholder="Anno"
                    className="w-[100px]"
                    clearable={false}
                  />
                  {effectiveAnnoCompare ? (
                    <div className="flex items-center gap-1">
                      <span className="text-xs text-muted-foreground">vs</span>
                      <Autocomplete
                        options={yearCompareOptions}
                        value={String(effectiveAnnoCompare)}
                        onChange={(val) => setIstatAnnoCompare(val ? Number(val) : null)}
                        placeholder="Anno"
                        className="w-[100px]"
                        clearable={false}
                      />
                      <button
                        onClick={() => setIstatAnnoCompare(null)}
                        className="p-1 text-muted-foreground hover:text-foreground transition-colors"
                        title="Rimuovi confronto anno"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ) : yearCompareOptions.length > 0 && (
                    <button
                      onClick={() => setIstatAnnoCompare(yearCompareOptions[0] ? Number(yearCompareOptions[0].value) : null)}
                      className="flex items-center gap-1 px-2 py-1.5 text-xs text-muted-foreground hover:text-foreground border border-dashed border-border rounded-lg hover:border-[#284899] transition-colors"
                      title="Confronta con altro anno"
                    >
                      <Plus className="h-3 w-3" />
                      vs anno
                    </button>
                  )}
                </div>
              )}

              <Autocomplete
                options={RESIDENZE.map(r => ({ value: r.value, label: r.label }))}
                value={istatResidenza}
                onChange={(val) => setIstatResidenza(val || "mondo")}
                placeholder="Provenienza"
                className="min-w-[170px]"
                clearable={false}
              />

              <Autocomplete
                options={TIPOLOGIE.map(t => ({ value: t.value, label: t.label }))}
                value={istatTipologia}
                onChange={(val) => setIstatTipologia(val || "totale")}
                placeholder="Tipo struttura"
                className="min-w-[190px]"
                clearable={false}
              />

              <div className="flex rounded-lg border border-border overflow-hidden">
                {[
                  { id: "bar" as const, icon: BarChart3, label: "Barre" },
                  { id: "line" as const, icon: LineChart, label: "Linea" },
                ].map((view) => (
                  <button
                    key={view.id}
                    onClick={() => setChartView(view.id)}
                    className={cn(
                      "flex items-center gap-1.5 px-3 py-2 text-sm transition-colors",
                      chartView === view.id
                        ? "bg-[#284899] text-white"
                        : "bg-card text-muted-foreground hover:text-foreground hover:bg-secondary/50"
                    )}
                  >
                    <view.icon className="h-4 w-4" />
                    {view.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Stats Cards — from ISTAT data */}
            {currentIndicator && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {(() => {
                  const ci = currentIndicator;
                  const cmp = compareIndicator;
                  function deltaVal(a: number | null, b: number | null, isPercent = false): { text: string; positive: boolean } | null {
                    if (!cmp || a === null || b === null || b === 0) return null;
                    const diff = isPercent ? a - b : ((a - b) / Math.abs(b)) * 100;
                    const sign = diff > 0 ? "+" : "";
                    return { text: `${sign}${diff.toFixed(1)}${isPercent ? "pp" : "%"}`, positive: diff > 0 };
                  }
                  return [
                    {
                      icon: Sun,
                      bg: "bg-amber-500/20", iconColor: "text-amber-500",
                      value: monthNamesFull[(ci.mese_picco || 1) - 1],
                      sub: formatNum(ci.p_max) + " presenze",
                      label: "Mese di picco",
                      compareValue: cmp ? monthNamesFull[(cmp.mese_picco || 1) - 1] : null,
                      delta: null as { text: string; positive: boolean } | null,
                    },
                    {
                      icon: TrendingUp,
                      ...(ci.sr !== null && ci.sr > 4
                        ? { bg: "bg-red-500/20", iconColor: "text-red-500" }
                        : ci.sr !== null && ci.sr >= 2
                          ? { bg: "bg-gray-400/20", iconColor: "text-gray-400" }
                          : { bg: "bg-emerald-500/20", iconColor: "text-emerald-500" }),
                      value: ci.sr !== null ? ci.sr.toFixed(1) : "-",
                      sub: "Pmax / Pmin",
                      label: "Tasso di stagionalità (SR)",
                      compareValue: cmp?.sr !== null && cmp?.sr !== undefined ? cmp.sr.toFixed(1) : null,
                      delta: deltaVal(ci.sr, cmp?.sr ?? null),
                    },
                    {
                      icon: BarChart3,
                      ...(ci.pf !== null && ci.pf > 4
                        ? { bg: "bg-red-500/20", iconColor: "text-red-500" }
                        : ci.pf !== null && ci.pf >= 2
                          ? { bg: "bg-gray-400/20", iconColor: "text-gray-400" }
                          : { bg: "bg-emerald-500/20", iconColor: "text-emerald-500" }),
                      value: ci.pf !== null ? ci.pf.toFixed(2) : "-",
                      sub: "Pmax / Pmedio",
                      label: "Fattore di picco stagionale (PF)",
                      compareValue: cmp?.pf !== null && cmp?.pf !== undefined ? cmp.pf.toFixed(2) : null,
                      delta: deltaVal(ci.pf, cmp?.pf ?? null),
                    },
                    {
                      icon: Target,
                      ...(ci.c3_pct !== null && ci.c3_pct > 50
                        ? { bg: "bg-red-500/20", iconColor: "text-red-500" }
                        : { bg: "bg-gray-400/20", iconColor: "text-gray-400" }),
                      value: ci.c3_pct !== null ? ci.c3_pct + "%" : "-",
                      sub: "Top 3 mesi / Totale",
                      label: "Concentrazione domanda (C3%)",
                      compareValue: cmp?.c3_pct !== null && cmp?.c3_pct !== undefined ? cmp.c3_pct + "%" : null,
                      delta: deltaVal(ci.c3_pct, cmp?.c3_pct ?? null, true),
                    },
                  ];
                })().map((card, i) => (
                  <motion.div key={card.label} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }}>
                    <Card>
                      <CardContent className="p-3">
                        <div className="flex items-center gap-3">
                          <div className={cn("w-9 h-9 rounded-lg flex items-center justify-center shrink-0", card.bg)}>
                            <card.icon className={cn("h-4 w-4", card.iconColor)} />
                          </div>
                          <div className="min-w-0 flex-1">
                            {card.compareValue !== null ? (
                              <>
                                <div className="flex items-baseline gap-2">
                                  <span className="text-lg font-bold leading-tight">{card.value}</span>
                                  <span className="text-xs text-muted-foreground">{effectiveAnno}</span>
                                </div>
                                <div className="flex items-baseline gap-2 mt-0.5">
                                  <span className="text-sm text-muted-foreground font-medium">{card.compareValue}</span>
                                  <span className="text-[10px] text-muted-foreground/70">{effectiveAnnoCompare}</span>
                                  {card.delta && (
                                    <span className={cn("text-[10px] font-medium", card.delta.positive ? "text-emerald-500" : "text-red-500")}>
                                      {card.delta.text}
                                    </span>
                                  )}
                                </div>
                                <p className="text-[10px] text-muted-foreground/70 leading-tight mt-0.5">{card.label}</p>
                              </>
                            ) : (
                              <>
                                <p className="text-xl font-bold leading-tight">{card.value}</p>
                                <p className="text-[11px] text-muted-foreground leading-tight">{card.label}</p>
                              </>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            )}

            {/* Main Content */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* Chart Area */}
              <div className="lg:col-span-2">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      {chartView === "bar" ? (
                        <BarChart3 className="h-4 w-4 text-[#284899]" />
                      ) : (
                        <LineChart className="h-4 w-4 text-[#284899]" />
                      )}
                      Presenze mensili {effectiveAnno}{effectiveAnnoCompare ? ` vs ${effectiveAnnoCompare}` : ""}
                      {istatTerritorio && <Badge variant="secondary">{istatTerritorio}</Badge>}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {!istatTerritorio ? (
                      <TerritoryHeatmap
                        heatmap={territoryHeatmap}
                        onSelect={setIstatTerritorio}
                      />
                    ) : presenzeLoading ? (
                      <div className="flex items-center justify-center h-64">
                        <Spinner size="lg" />
                      </div>
                    ) : chartData ? (
                      <IstatChart
                        chartData={chartData}
                        chartType={chartView}
                        compareData={chartDataCompare}
                        anno={effectiveAnno}
                        annoCompare={effectiveAnnoCompare}
                        plGiorno={currentCapacity?.pl_prov ?? null}
                      />
                    ) : (
                      <div className="flex flex-col items-center justify-center h-64">
                        <BarChart3 className="h-12 w-12 text-muted-foreground opacity-30 mb-4" />
                        <h3 className="text-lg font-semibold mb-2">Nessun dato disponibile</h3>
                        <p className="text-muted-foreground text-sm">
                          Seleziona un territorio dal filtro
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Territory Comparison */}
                {istatTerritorio && (
                  <div className="mt-4">
                    <Button
                      variant="outline"
                      onClick={() => setShowComparison(!showComparison)}
                      className="mb-3 w-full py-2.5 text-sm"
                    >
                      <ArrowLeftRight className="h-4 w-4 mr-2" />
                      {showComparison ? "Nascondi confronto territoriale" : "Confronta con altre regioni / province"}
                    </Button>

                    {showComparison && (
                      <Card>
                        <CardHeader className="pb-2">
                          <CardTitle className="text-base flex items-center gap-2">
                            <ArrowLeftRight className="h-4 w-4 text-[#284899]" />
                            Presenze mensili {effectiveAnno} a confronto
                            <IndicatorTooltip text={"Come leggere il grafico\n\nIn questo grafico puoi confrontare più destinazioni (province) per capire come si distribuiscono i flussi turistici durante l'anno. Selezionando più territori, puoi vedere in quali mesi ciascuna destinazione ha più o meno presenze.\n\nI valori sono espressi in percentuale sul totale annuo (base 100). Ogni territorio è riportato su una scala comune, così da poter confrontare destinazioni anche con volumi molto differenti.\n\nValori più alti indicano mesi in cui si concentra la maggior parte dei turisti, valori più bassi periodi con minore affluenza. L'andamento del grafico aiuta a individuare i mesi di picco, la durata della stagione turistica e quanto i flussi sono distribuiti nel corso dell'anno."} />
                          </CardTitle>
                          <p className="text-xs text-muted-foreground">
                            {istatTerritorio}
                            {comparisonTerritories.length > 0 && ` vs ${comparisonTerritories.join(", ")}`}
                            {" · "}
                            {RESIDENZE.find(r => r.value === istatResidenza)?.label}
                            {" · "}
                            {TIPOLOGIE.find(t => t.value === istatTipologia)?.label}
                          </p>
                        </CardHeader>
                        <CardContent>
                          <div className="flex flex-wrap gap-2 mb-3">
                            {comparisonTerritories.map((t, idx) => (
                              <div key={t} className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border" style={{ borderColor: COMPARISON_COLORS[idx], color: COMPARISON_COLORS[idx] }}>
                                <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: COMPARISON_COLORS[idx] }} />
                                {t}
                                <button
                                  onClick={() => setComparisonTerritories(comparisonTerritories.filter((_, i) => i !== idx))}
                                  className="ml-0.5 hover:opacity-70"
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              </div>
                            ))}
                            {comparisonTerritories.length < 4 && (
                              <Autocomplete
                                options={comparisonOptions}
                                value=""
                                onChange={(val) => {
                                  if (val && !comparisonTerritories.includes(val)) {
                                    setComparisonTerritories([...comparisonTerritories, val]);
                                  }
                                }}
                                placeholder={comparisonTerritories.length === 0 ? "Aggiungi territorio..." : "Aggiungi..."}
                                className="min-w-[180px] max-w-[220px]"
                              />
                            )}
                          </div>
                          {comparisonTerritories.length > 0 && (
                            <ComparisonChart
                              primarySeries={presenze}
                              primaryLabel={istatTerritorio}
                              compareSeries={comparisonTerritories.map((_, i) => compTerrPresenzeAll[i])}
                              compareLabels={comparisonTerritories}
                            />
                          )}
                        </CardContent>
                      </Card>
                    )}
                  </div>
                )}

                {/* Suggestions */}
                {currentIndicator && currentIndicator.sr !== null && currentIndicator.sr > 5 && (
                  <Card className="mt-6 border-[#284899]/30 bg-[#284899]/5">
                    <CardHeader>
                      <CardTitle className="text-base flex items-center gap-2">
                        <Lightbulb className="h-4 w-4 text-[#284899]" />
                        Suggerimenti per {istatTerritorio}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        <p className="text-sm text-muted-foreground">
                          Stagionalita elevata (SR = {currentIndicator.sr}). Il {currentIndicator.c3_pct}% delle presenze si concentra in soli 3 mesi.
                          Mese piu debole: <span className="font-medium text-foreground">{monthNames[(currentIndicator.mese_minimo || 1) - 1]}</span>.
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {[
                            { icon: Target, title: "Crea Pianificazioni Speciali", desc: "Offri pianificazioni a prezzo ridotto per i periodi di bassa stagione" },
                            { icon: Info, title: "Promuovi Eventi Locali", desc: "Organizza eventi tematici per attirare visitatori" },
                          ].map((s, i) => (
                            <div key={i} className="flex items-start gap-2 p-3 rounded-lg bg-background border border-border">
                              <s.icon className="h-4 w-4 text-[#284899] mt-0.5 flex-shrink-0" />
                              <div>
                                <h5 className="font-medium text-sm">{s.title}</h5>
                                <p className="text-xs text-muted-foreground">{s.desc}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>

              {/* Sidebar */}
              <div className="space-y-4">
                {/* Indicators panel */}
                {currentIndicator && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center justify-between">
                        <span>Dettaglio indicatori</span>
                        {effectiveAnnoCompare && (
                          <span className="text-xs font-normal text-muted-foreground">
                            {effectiveAnno} vs {effectiveAnnoCompare}
                          </span>
                        )}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2 text-sm">
                      {/* Presenze totali */}
                      <div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Presenze totali annue</span>
                          <span className="font-medium">{formatNum(currentIndicator.p_totale)}</span>
                        </div>
                        {compareIndicator && (
                          <div className="flex justify-end gap-2 text-xs text-muted-foreground/70">
                            <span>{effectiveAnnoCompare}: {formatNum(compareIndicator.p_totale)}</span>
                            {compareIndicator.p_totale > 0 && (
                              <span className={cn("font-medium", currentIndicator.p_totale > compareIndicator.p_totale ? "text-emerald-500" : "text-red-500")}>
                                {currentIndicator.p_totale > compareIndicator.p_totale ? "+" : ""}
                                {(((currentIndicator.p_totale - compareIndicator.p_totale) / compareIndicator.p_totale) * 100).toFixed(1)}%
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                      <div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Media mensile presenze</span>
                          <span className="font-medium">{formatNum(currentIndicator.p_medio)}</span>
                        </div>
                        {compareIndicator && (
                          <div className="flex justify-end text-xs text-muted-foreground/70">
                            <span>{effectiveAnnoCompare}: {formatNum(compareIndicator.p_medio)}</span>
                          </div>
                        )}
                      </div>
                      <hr className="my-2" />
                      {([
                        {
                          key: "sr", label: "Tasso di stagionalità", val: currentIndicator.sr, cmpVal: compareIndicator?.sr ?? null, thresholds: [2, 4] as const,
                          tooltip: "Misura quanto le presenze turistiche sono concentrate in pochi mesi dell'anno rispetto a una distribuzione più equilibrata. I risultati sono espressi in valore relativo.\n\nRosso (stagionalità alta): le presenze sono fortemente concentrate in pochi mesi → elevata dipendenza dalla stagione e maggiore rischio di squilibri.\nGrigio (stagionalità media): le presenze sono distribuite in modo parzialmente equilibrato → presenza di stagionalità, ma con una certa continuità durante l'anno.\nVerde (stagionalità bassa): le presenze sono distribuite in modo uniforme durante l'anno → destinazione più stabile e meno dipendente da pochi mesi.",
                        },
                        {
                          key: "si", label: "Intensità stagionale", val: currentIndicator.si, cmpVal: compareIndicator?.si ?? null, thresholds: null,
                          tooltip: "Misura quanto è ampia la differenza tra il mese con più presenze e quello con meno presenze, in rapporto al livello medio annuale. Indica quanto i flussi turistici si discostano dalla media nel corso dell'anno (numero puro, confrontabile tra territori e periodi).\n\nValori elevati → forte escursione stagionale\nValori medi → escursione moderata\nValori bassi → escursione contenuta",
                        },
                        {
                          key: "pf", label: "Fattore di picco stagionale", val: currentIndicator.pf, cmpVal: compareIndicator?.pf ?? null, thresholds: [2, 4] as const,
                          tooltip: "Misura quanto il mese con il maggior numero di presenze supera il livello medio mensile dell'anno. Indica l'intensità del picco stagionale rispetto alla normalità della destinazione (numero puro, confrontabile).\n\nVerde → picco contenuto: il mese di maggiore affluenza è poco distante dalla media.\nGrigio → picco rilevante: il mese di punta supera in modo evidente la media.\nRosso → picco molto marcato: il mese di punta è molto superiore alla media, con elevata pressione nei mesi di alta stagione.",
                        },
                      ] as const).map(row => {
                        const dotColor = row.val !== null && row.thresholds
                          ? (row.val as number) > row.thresholds[1] ? "#d62728" : (row.val as number) >= row.thresholds[0] ? "#9ca3af" : "#2ca02c"
                          : undefined;
                        return (
                          <div key={row.key}>
                            <div className="flex justify-between items-center">
                              <span className="text-muted-foreground flex items-center gap-1">
                                {row.label}
                                {row.tooltip && <IndicatorTooltip text={row.tooltip} />}
                              </span>
                              <span className="font-medium flex items-center gap-1">
                                {formatNum(row.val)}
                                {dotColor && (
                                  <span
                                    className="inline-block w-3 h-3 rounded-full"
                                    style={{ backgroundColor: dotColor }}
                                  />
                                )}
                              </span>
                            </div>
                            {compareIndicator && row.cmpVal !== null && (
                              <div className="flex justify-end text-xs text-muted-foreground/70">
                                <span>{effectiveAnnoCompare}: {formatNum(row.cmpVal)}</span>
                              </div>
                            )}
                          </div>
                        );
                      })}
                      <hr className="my-2" />
                      {/* Concentrazione alta (top 3 mesi) */}
                      <div>
                        <div className="flex justify-between items-center">
                          <span className="text-muted-foreground flex flex-col">
                            <span className="flex items-center gap-1">
                              Concentrazione alta
                              <IndicatorTooltip text={"Quota percentuale di presenze turistiche annuali concentrata nei tre mesi con il valore più alto. Misura quanto il turismo si concentra nei periodi di punta rispetto al totale dell'anno.\n\nValori elevati → forte concentrazione: una quota rilevante delle presenze si concentra in pochi mesi.\nValori intermedi → concentrazione moderata: i mesi di punta incidono in modo significativo, ma la domanda è distribuita anche negli altri periodi.\nValori bassi → distribuzione equilibrata: presenze distribuite in modo più uniforme nell'anno."} />
                            </span>
                            <span className="text-xs">(3 mesi di picco)</span>
                          </span>
                          <span className="font-medium text-red-500">
                            {currentIndicator.c3_pct !== null ? currentIndicator.c3_pct + "%" : "-"}
                          </span>
                        </div>
                        {chartData && (
                          <p className="text-xs text-red-500 mt-0.5">
                            {Array.from(chartData.top3).sort((a, b) => a - b).map(i => monthNames[i]).join(" – ")}
                          </p>
                        )}
                        {compareIndicator && compareIndicator.c3_pct !== null && (
                          <div className="flex justify-end text-xs text-muted-foreground/70 mt-0.5">
                            <span>{effectiveAnnoCompare}: {compareIndicator.c3_pct}%</span>
                          </div>
                        )}
                      </div>
                      {/* Concentrazione bassa (bottom 3 mesi) */}
                      {chartData && currentIndicator.p_totale > 0 && (
                        <div>
                          <div className="flex justify-between items-center">
                            <span className="text-muted-foreground flex flex-col">
                              <span className="flex items-center gap-1">
                                Concentrazione bassa
                                <IndicatorTooltip text={"Quota percentuale di presenze turistiche annuali concentrata nei tre mesi con il valore più basso. Misura quanto incidono i periodi di minore attività sul totale dell'anno.\n\nValori molto bassi → mesi deboli poco rilevanti: la domanda è fortemente concentrata nei mesi di punta.\nValori intermedi → presenza moderata nei mesi deboli: una certa continuità della domanda fuori picco.\nValori elevati → buona distribuzione anche nei mesi deboli: la destinazione mantiene flussi turistici anche fuori stagione."} />
                              </span>
                              <span className="text-xs">(3 mesi di minimo)</span>
                            </span>
                            <span className="font-medium text-emerald-500">
                              {(() => {
                                const bottom3Sum = Array.from(chartData.bottom3).reduce((s, i) => s + chartData.monthly[i], 0);
                                return ((bottom3Sum / currentIndicator.p_totale) * 100).toFixed(1) + "%";
                              })()}
                            </span>
                          </div>
                          <p className="text-xs text-emerald-500 mt-0.5">
                            {Array.from(chartData.bottom3).sort((a, b) => a - b).map(i => monthNames[i]).join(" – ")}
                          </p>
                        </div>
                      )}
                      {currentCapacity && (
                        <>
                          <hr className="my-2" />
                          <div className="flex justify-between items-center">
                            <span className="text-muted-foreground flex items-center gap-1">
                              Capacità posti letto giornaliera
                              <IndicatorTooltip text={"Indica il numero totale di posti letto disponibili in un giorno all'interno della provincia, considerando l'insieme delle strutture ricettive presenti sul territorio. Rappresenta la capacità ricettiva giornaliera del sistema turistico."} />
                            </span>
                            <span className="font-medium">{formatNum(currentCapacity.pl_prov)}</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-muted-foreground flex items-center gap-1">
                              Capacità posti letto mensile
                              <IndicatorTooltip text={"Indica il numero massimo di presenze turistiche che la provincia può teoricamente ospitare in un mese, considerando i posti letto disponibili e i giorni del periodo. Rappresenta quindi la capacità ricettiva mensile del territorio."} />
                            </span>
                            <span className="font-medium">{formatNum(currentCapacity.pl_prov * 30)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Capacità massima annua (posti letto-giorno)</span>
                            <span className="font-medium">{formatNum(currentCapacity.cpla_prov)}</span>
                          </div>
                          {currentIndicator && currentCapacity.pl_prov > 0 && (
                            <div className="flex justify-between items-center">
                              <span className="text-muted-foreground flex items-center gap-1">
                                Tasso di occupazione annuo provinciale
                                <IndicatorTooltip text={"Misura il livello di utilizzo della capacità ricettiva nell'anno, mettendo in rapporto le presenze turistiche complessive con la capacità massima disponibile. Indica quanto il sistema ricettivo è stato utilizzato nel corso dell'anno rispetto al suo potenziale."} />
                              </span>
                              <span className="font-medium">
                                {((currentIndicator.p_totale / (currentCapacity.pl_prov * 365)) * 100).toFixed(1)}%
                              </span>
                            </div>
                          )}
                        </>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Monthly Breakdown */}
                {istatTerritorio && chartData && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center justify-between">
                        <span>Dettaglio Mensile</span>
                        {effectiveAnnoCompare && (
                          <span className="text-xs font-normal text-muted-foreground">
                            {effectiveAnno} vs {effectiveAnnoCompare}
                          </span>
                        )}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-1.5">
                        {chartData.monthly.map((val, i) => {
                          const season = getSeasonFromMonth(i + 1);
                          const SeasonIcon = seasonIcons[season];
                          const globalBarMax = Math.max(chartData.max, chartDataCompare?.max || 0, 1);
                          const pct = globalBarMax > 0 ? (val / globalBarMax) * 100 : 0;
                          const isTop = chartData.top3.has(i);
                          const isBottom = chartData.bottom3.has(i);
                          const cmpVal = chartDataCompare?.monthly[i];
                          const cmpPct = cmpVal !== undefined && globalBarMax > 0 ? (cmpVal / globalBarMax) * 100 : 0;
                          const diffPct = cmpVal && cmpVal > 0 ? ((val - cmpVal) / cmpVal) * 100 : null;
                          return (
                            <div
                              key={i}
                              className={cn(
                                "flex items-center gap-2 p-1.5 rounded-lg transition-colors",
                                isBottom && "bg-emerald-500/10 border border-emerald-500/20",
                                isTop && "bg-red-500/10 border border-red-500/20"
                              )}
                            >
                              <SeasonIcon className={cn(
                                "h-3.5 w-3.5 shrink-0",
                                season === "summer" && "text-[#284899]",
                                season === "winter" && "text-blue-400",
                                season === "spring" && "text-emerald-500",
                                season === "autumn" && "text-[#284899]"
                              )} />
                              <span className="text-xs font-medium w-8">{monthNames[i]}</span>
                              <div className="flex-1 space-y-0.5">
                                <div className="h-2 rounded-full bg-secondary overflow-hidden">
                                  <div
                                    className={cn(
                                      "h-full rounded-full transition-all",
                                      isTop && "bg-red-500",
                                      isBottom && "bg-emerald-500",
                                      !isTop && !isBottom && "bg-[#284899]"
                                    )}
                                    style={{ width: `${pct}%` }}
                                  />
                                </div>
                                {chartDataCompare && (
                                  <div className="h-1.5 rounded-full bg-secondary/50 overflow-hidden">
                                    <div
                                      className="h-full rounded-full bg-[#284899]/30"
                                      style={{ width: `${cmpPct}%` }}
                                    />
                                  </div>
                                )}
                              </div>
                              <div className="text-right shrink-0">
                                <span className="text-xs font-medium block w-14 text-right">
                                  {formatNum(val)}
                                </span>
                                {chartDataCompare && cmpVal !== undefined && (
                                  <div className="flex items-center justify-end gap-1">
                                    <span className="text-[10px] text-muted-foreground/70">{formatNum(cmpVal)}</span>
                                    {diffPct !== null && (
                                      <span className={cn("text-[10px] font-medium", diffPct > 0 ? "text-emerald-500" : "text-red-500")}>
                                        {diffPct > 0 ? "+" : ""}{diffPct.toFixed(0)}%
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>
                              {occupancyPct && (
                                <div className="text-right shrink-0 w-12 flex items-center justify-end gap-0.5">
                                  <span className="text-[11px] font-medium text-[#284899]">
                                    {occupancyPct[i].toFixed(0)}%
                                  </span>
                                  {i === 0 && (
                                    <IndicatorTooltip text={"Misura il livello di utilizzo della capacità ricettiva nel mese, mettendo in rapporto le presenze turistiche registrate con la capacità massima disponibile nello stesso periodo. Indica quindi quanto i posti letto sono stati effettivamente utilizzati rispetto al totale disponibile."} />
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Legend */}
                <Card>
                  <CardContent className="p-4">
                    <h4 className="text-sm font-medium mb-3">Legenda</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-red-500" />
                        <span className="text-muted-foreground">Top 3 mesi (alta stagione)</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-[#284899]" />
                        <span className="text-muted-foreground">Stagione intermedia</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-emerald-500" />
                        <span className="text-muted-foreground">Bottom 3 mesi (bassa stagione)</span>
                      </div>
                      <hr className="my-2" />
                      <p className="text-xs text-muted-foreground">Classificazione indicatori:</p>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-[#2ca02c]" />
                        <span className="text-muted-foreground">Basso</span>
                        <div className="w-3 h-3 rounded-full bg-[#9ca3af] ml-2" />
                        <span className="text-muted-foreground">Medio</span>
                        <div className="w-3 h-3 rounded-full bg-[#d62728] ml-2" />
                        <span className="text-muted-foreground">Alto</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Preferences Modal removed — no longer tracked */}
          </>
        )}
      </div>
    </div>
  );
}

// ============================================
// SUB-COMPONENTS
// ============================================

function IndicatorTooltip({ text }: { text: string }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  return (
    <div className="relative inline-flex items-center" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="inline-flex items-center justify-center text-muted-foreground/50 hover:text-[#284899] transition-colors leading-none"
      >
        <Info className="h-3.5 w-3.5" />
      </button>
      {open && (
        <div className="absolute left-0 top-full mt-1.5 z-50 w-64 p-3 rounded-lg bg-card border border-border shadow-lg text-xs font-normal text-muted-foreground leading-relaxed whitespace-pre-line text-left">
          {text}
        </div>
      )}
    </div>
  );
}

//
// ============================================

type ChartDataType = { monthly: number[]; max: number; mean: number; top3: Set<number>; bottom3: Set<number> };

// Giorni del mese (anno non bisestile) — usati per il calcolo dell'occupancy mensile
const DAYS_IN_MONTH = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

function IstatChart({
  chartData,
  chartType,
  compareData,
  anno,
  annoCompare,
  plGiorno,
}: {
  chartData: ChartDataType;
  chartType: "bar" | "line";
  compareData?: ChartDataType | null;
  anno?: number;
  annoCompare?: number | null;
  plGiorno?: number | null;
}) {
  const { monthly, top3, bottom3 } = chartData;
  const hasCompare = !!compareData;
  const globalMax = Math.max(chartData.max, compareData?.max || 0, 1);
  const mean = chartData.mean;

  // Occupancy mensile = (P_mese / (PL_giorno × Giorni_mese)) × 100
  // Disponibile solo a livello provinciale (plGiorno passato dal parent).
  const occupancyPct = useMemo(
    () =>
      plGiorno && plGiorno > 0
        ? monthly.map((p, i) => (p / (plGiorno * DAYS_IN_MONTH[i])) * 100)
        : null,
    [monthly, plGiorno]
  );

  const [hover, setHover] = useState<{ x: number; y: number; label: string; value: string; color: string } | null>(null);

  const svgW = 600;
  const svgH = 280;
  const padL = 60;
  const padR = 20;
  const padT = 28;
  const padB = 40;
  const plotW = svgW - padL - padR;
  const plotH = svgH - padT - padB;
  const groupW = plotW / 12;
  const singleBarW = groupW * 0.6;
  const dualBarW = groupW * 0.35;

  const scaleY = (v: number) => padT + plotH - (globalMax > 0 ? (v / globalMax) * plotH : 0);
  const scaleX = (i: number) => padL + (i + 0.5) * groupW;

  const linePoints = monthly.map((v, i) => `${scaleX(i)},${scaleY(v)}`).join(" ");
  const compareLinePoints = compareData?.monthly.map((v, i) => `${scaleX(i)},${scaleY(v)}`).join(" ");

  const colorFor = (isT: boolean, isB: boolean) => isT ? "#ef4444" : isB ? "#10b981" : "#284899";

  return (
    <div className="relative">
      <svg viewBox={`0 0 ${svgW} ${svgH}`} className="w-full" onMouseLeave={() => setHover(null)}>
        {/* Grid lines */}
        {[0, 0.25, 0.5, 0.75, 1].map(frac => {
          const y = padT + plotH * (1 - frac);
          return (
            <g key={frac}>
              <line x1={padL} y1={y} x2={svgW - padR} y2={y} stroke="#e5e7eb" strokeWidth={0.5} />
              <text x={padL - 6} y={y + 4} textAnchor="end" fontSize={10} fill="#9ca3af">
                {formatNum(globalMax * frac)}
              </text>
            </g>
          );
        })}

        {/* Bars */}
        {monthly.map((v, i) => {
          const isTop = top3.has(i);
          const isBottom = bottom3.has(i);
          const baseline = padT + plotH;

          if (chartType === "bar") {
            const cx = scaleX(i);
            if (hasCompare) {
              const aH = globalMax > 0 ? (v / globalMax) * plotH : 0;
              const bVal = compareData!.monthly[i];
              const bH = globalMax > 0 ? (bVal / globalMax) * plotH : 0;
              const bIsTop = compareData!.top3.has(i);
              const bIsBottom = compareData!.bottom3.has(i);
              const aColor = colorFor(isTop, isBottom);
              const bColor = colorFor(bIsTop, bIsBottom);
              return (
                <g key={i}>
                  <motion.rect
                    x={cx - dualBarW - 1}
                    width={dualBarW}
                    fill={aColor}
                    opacity={0.85}
                    rx={2}
                    initial={{ height: 0 }}
                    animate={{ height: aH }}
                    y={baseline - aH}
                    transition={{ delay: i * 0.04, duration: 0.3 }}
                    style={{ cursor: "pointer" }}
                    onMouseEnter={() => setHover({
                      x: ((cx - dualBarW / 2 - 1) / svgW) * 100,
                      y: (scaleY(v) / svgH) * 100,
                      label: `${monthNamesFull[i]} ${anno ?? ""}`,
                      value: formatNum(v),
                      color: aColor,
                    })}
                  />
                  <motion.rect
                    x={cx + 1}
                    width={dualBarW}
                    fill={bColor}
                    opacity={0.45}
                    rx={2}
                    initial={{ height: 0 }}
                    animate={{ height: bH }}
                    y={baseline - bH}
                    transition={{ delay: i * 0.04, duration: 0.3 }}
                    style={{ cursor: "pointer" }}
                    onMouseEnter={() => setHover({
                      x: ((cx + dualBarW / 2 + 1) / svgW) * 100,
                      y: (scaleY(bVal) / svgH) * 100,
                      label: `${monthNamesFull[i]} ${annoCompare ?? ""}`,
                      value: formatNum(bVal),
                      color: bColor,
                    })}
                  />
                </g>
              );
            }
            const barH = globalMax > 0 ? (v / globalMax) * plotH : 0;
            const sColor = colorFor(isTop, isBottom);
            return (
              <motion.rect
                key={i}
                x={cx - singleBarW / 2}
                width={singleBarW}
                fill={sColor}
                opacity={0.8}
                rx={2}
                initial={{ height: 0 }}
                animate={{ height: barH }}
                y={baseline - barH}
                transition={{ delay: i * 0.04, duration: 0.3 }}
                style={{ cursor: "pointer" }}
                onMouseEnter={() => setHover({
                  x: (cx / svgW) * 100,
                  y: (scaleY(v) / svgH) * 100,
                  label: `${monthNamesFull[i]} ${anno ?? ""}`,
                  value: formatNum(v),
                  color: sColor,
                })}
              />
            );
          }
          // Background highlight bars for line chart
          if (hasCompare) {
            const cx = scaleX(i);
            const aH = globalMax > 0 ? (v / globalMax) * plotH : 0;
            const bVal = compareData!.monthly[i];
            const bH = globalMax > 0 ? (bVal / globalMax) * plotH : 0;
            const bIsTop = compareData!.top3.has(i);
            const bIsBottom = compareData!.bottom3.has(i);
            const fillA = isTop ? "rgba(239,68,68,0.15)" : isBottom ? "rgba(16,185,129,0.15)" : "rgba(40,72,153,0.08)";
            const fillB = bIsTop ? "rgba(239,68,68,0.10)" : bIsBottom ? "rgba(16,185,129,0.10)" : "rgba(40,72,153,0.05)";
            return (
              <g key={i}>
                <rect x={cx - dualBarW - 1} y={baseline - aH} width={dualBarW} height={aH} fill={fillA} rx={2} />
                <rect x={cx + 1} y={baseline - bH} width={dualBarW} height={bH} fill={fillB} rx={2} />
              </g>
            );
          }
          const barH = globalMax > 0 ? (v / globalMax) * plotH : 0;
          const fill = isTop ? "rgba(239,68,68,0.12)" : isBottom ? "rgba(16,185,129,0.12)" : "rgba(156,163,175,0.06)";
          return (
            <rect
              key={i}
              x={scaleX(i) - singleBarW / 2}
              y={baseline - barH}
              width={singleBarW}
              height={barH}
              fill={fill}
              rx={2}
            />
          );
        })}

        {/* Value labels */}
        {!hasCompare && monthly.map((v, i) => {
          const isTop = top3.has(i);
          const isBottom = bottom3.has(i);
          if (!isTop && !isBottom) return null;
          return (
            <text
              key={`lbl-${i}`}
              x={scaleX(i)}
              y={scaleY(v) - 6}
              textAnchor="middle"
              fontSize={10}
              fontWeight={isTop ? 700 : 400}
              fill={isTop ? "#ef4444" : "#10b981"}
            >
              {formatNum(v)}
            </text>
          );
        })}
        {/* Dual-year value labels on top3/bottom3 bars */}
        {hasCompare && chartType === "bar" && monthly.map((v, i) => {
          const isTop = top3.has(i);
          const isBottom = bottom3.has(i);
          if (!isTop && !isBottom) return null;
          const cx = scaleX(i);
          return (
            <text
              key={`lbl-a-${i}`}
              x={cx - dualBarW / 2 - 1}
              y={scaleY(v) - 4}
              textAnchor="middle"
              fontSize={8}
              fontWeight={700}
              fill={isTop ? "#ef4444" : "#10b981"}
            >
              {formatNum(v)}
            </text>
          );
        })}
        {hasCompare && chartType === "bar" && compareData && compareData.monthly.map((v, i) => {
          const bIsTop = compareData.top3.has(i);
          const bIsBottom = compareData.bottom3.has(i);
          if (!bIsTop && !bIsBottom) return null;
          const cx = scaleX(i);
          return (
            <text
              key={`lbl-b-${i}`}
              x={cx + dualBarW / 2 + 1}
              y={scaleY(v) - 4}
              textAnchor="middle"
              fontSize={8}
              fontWeight={400}
              fill={bIsTop ? "#ef4444" : "#10b981"}
              opacity={0.6}
            >
              {formatNum(v)}
            </text>
          );
        })}

        {/* Mean line */}
        <line
          x1={padL} y1={scaleY(mean)}
          x2={svgW - padR} y2={scaleY(mean)}
          stroke="#9ca3af" strokeWidth={1} strokeDasharray="6,4"
        />

        {/* Comparison year line (always shown when available) */}
        {hasCompare && chartType === "line" && compareLinePoints && (
          <>
            <polyline
              points={compareLinePoints}
              fill="none"
              stroke="#284899"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
              opacity={0.5}
              strokeDasharray="6,3"
            />
            {compareData!.monthly.map((v, i) => {
              const bIsTop = compareData!.top3.has(i);
              const bIsBottom = compareData!.bottom3.has(i);
              return (
                <circle
                  key={`cmp-${i}`}
                  cx={scaleX(i)}
                  cy={scaleY(v)}
                  r={3}
                  fill={bIsTop ? "#ef4444" : bIsBottom ? "#10b981" : "#284899"}
                  opacity={0.5}
                  stroke="white"
                  strokeWidth={1}
                />
              );
            })}
          </>
        )}

        {/* Primary line chart overlay */}
        {chartType === "line" && (
          <>
            <defs>
              <linearGradient id="lineGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#284899" stopOpacity={0.2} />
                <stop offset="100%" stopColor="#284899" stopOpacity={0} />
              </linearGradient>
            </defs>
            {!hasCompare && (
              <polygon
                points={`${scaleX(0)},${padT + plotH} ${linePoints} ${scaleX(11)},${padT + plotH}`}
                fill="url(#lineGrad)"
              />
            )}
            <polyline
              points={linePoints}
              fill="none"
              stroke="#284899"
              strokeWidth={2.5}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            {monthly.map((v, i) => (
              <circle
                key={i}
                cx={scaleX(i)}
                cy={scaleY(v)}
                r={3.5}
                fill={top3.has(i) ? "#ef4444" : bottom3.has(i) ? "#10b981" : "#284899"}
                stroke="white"
                strokeWidth={1.5}
              />
            ))}
          </>
        )}

        {/* X axis: month names above, occupancy % below */}
        {monthNames.map((label, i) => (
          <text key={i} x={scaleX(i)} y={padT + plotH + 14} textAnchor="middle" fontSize={11} fill="#6b7280">
            {label}
          </text>
        ))}

        {occupancyPct && occupancyPct.map((occ, i) => (
          <text
            key={`occ-${i}`}
            x={scaleX(i)}
            y={svgH - 8}
            textAnchor="middle"
            fontSize={9}
            fill="#284899"
            fontWeight={500}
          >
            {occ.toFixed(0)}%
          </text>
        ))}

        {/* Invisible hover zones for line chart */}
        {chartType === "line" && monthly.map((v, i) => {
          const cx = scaleX(i);
          if (hasCompare) {
            const bVal = compareData!.monthly[i];
            const aColor = colorFor(top3.has(i), bottom3.has(i));
            const bColor = colorFor(compareData!.top3.has(i), compareData!.bottom3.has(i));
            return (
              <g key={`hz-${i}`}>
                <rect
                  x={cx - dualBarW - 1}
                  y={padT}
                  width={dualBarW}
                  height={plotH}
                  fill="transparent"
                  style={{ cursor: "pointer" }}
                  onMouseEnter={() => setHover({
                    x: ((cx - dualBarW / 2 - 1) / svgW) * 100,
                    y: (scaleY(v) / svgH) * 100,
                    label: `${monthNamesFull[i]} ${anno ?? ""}`,
                    value: formatNum(v),
                    color: aColor,
                  })}
                />
                <rect
                  x={cx + 1}
                  y={padT}
                  width={dualBarW}
                  height={plotH}
                  fill="transparent"
                  style={{ cursor: "pointer" }}
                  onMouseEnter={() => setHover({
                    x: ((cx + dualBarW / 2 + 1) / svgW) * 100,
                    y: (scaleY(bVal) / svgH) * 100,
                    label: `${monthNamesFull[i]} ${annoCompare ?? ""}`,
                    value: formatNum(bVal),
                    color: bColor,
                  })}
                />
              </g>
            );
          }
          return (
            <rect
              key={`hz-${i}`}
              x={cx - singleBarW / 2}
              y={padT}
              width={singleBarW}
              height={plotH}
              fill="transparent"
              style={{ cursor: "pointer" }}
              onMouseEnter={() => setHover({
                x: (cx / svgW) * 100,
                y: (scaleY(v) / svgH) * 100,
                label: `${monthNamesFull[i]} ${anno ?? ""}`,
                value: formatNum(v),
                color: colorFor(top3.has(i), bottom3.has(i)),
              })}
            />
          );
        })}
      </svg>

      {/* Hover tooltip */}
      {hover && (
        <div
          className="absolute pointer-events-none z-10 px-2 py-1 rounded-md bg-card border shadow-lg text-xs font-medium whitespace-nowrap -translate-x-1/2 -translate-y-full -mt-1"
          style={{
            left: `${hover.x}%`,
            top: `${hover.y}%`,
            borderColor: hover.color,
          }}
        >
          <div className="text-muted-foreground text-[10px]">{hover.label}</div>
          <div className="font-bold" style={{ color: hover.color }}>{hover.value}</div>
        </div>
      )}

      <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground justify-center flex-wrap">
        {hasCompare && anno && (
          <span className="flex items-center gap-1.5 font-medium">
            <span className="w-3 h-3 rounded" style={{ backgroundColor: "#284899" }} /> {anno}
          </span>
        )}
        {hasCompare && annoCompare && (
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded border border-dashed border-[#284899]" style={{ backgroundColor: "#284899", opacity: 0.45 }} /> {annoCompare}
          </span>
        )}
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded bg-red-500/80" /> Top 3
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded bg-emerald-500/80" /> Bottom 3
        </span>
        <span className="flex items-center gap-1">
          <span className="w-8 border-t border-dashed border-gray-400" /> Media
        </span>
        {occupancyPct && (
          <span className="flex items-center gap-1">
            <span className="text-[11px] font-medium text-[#284899]">%</span>
            <span>Tasso di occupazione mensile</span>
            <IndicatorTooltip text={"Misura il livello di utilizzo della capacità ricettiva nel mese, mettendo in rapporto le presenze turistiche registrate con la capacità massima disponibile nello stesso periodo. Indica quindi quanto i posti letto sono stati effettivamente utilizzati rispetto al totale disponibile."} />
          </span>
        )}
      </div>
    </div>
  );
}

function TerritoryHeatmap({
  heatmap,
  onSelect,
}: {
  heatmap: Record<string, { mese_picco: number; sr: number | null; p_totale: number }>;
  onSelect: (territory: string) => void;
}) {
  const [view, setView] = useState<"heatmap" | "tabella">("heatmap");

  const sorted = useMemo(() => {
    return Object.entries(heatmap).sort(([, a], [, b]) => b.p_totale - a.p_totale);
  }, [heatmap]);


  if (sorted.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <Spinner size="lg" />
        <p className="text-sm text-muted-foreground mt-4">Caricamento territori...</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* View toggle */}
      <div className="flex items-center gap-1 bg-secondary/50 rounded-lg p-0.5 w-fit">
        <button
          onClick={() => setView("heatmap")}
          className={cn(
            "px-3 py-1 rounded-md text-xs font-medium transition-colors",
            view === "heatmap" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
          )}
        >
          Heatmap
        </button>
        <button
          onClick={() => setView("tabella")}
          className={cn(
            "px-3 py-1 rounded-md text-xs font-medium transition-colors",
            view === "tabella" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
          )}
        >
          Tabella
        </button>
      </div>

      {view === "heatmap" ? (
        <div className="max-h-[400px] overflow-y-auto overflow-x-auto">
          {/* Month header */}
          <div className="flex items-center gap-0 sticky top-0 bg-card z-10 pb-1 border-b border-border">
            <span className="w-28 min-w-[7rem] text-xs font-medium text-muted-foreground shrink-0">Territorio</span>
            {monthNames.map((m) => (
              <span key={m} className="w-8 min-w-[2rem] text-center text-[10px] text-muted-foreground">{m}</span>
            ))}
          </div>
          {/* Rows */}
          {sorted.slice(0, 50).map(([territory, data]) => (
            <button
              key={territory}
              onClick={() => onSelect(territory)}
              className="flex items-center gap-0 w-full text-left py-0.5 rounded hover:bg-secondary/30 transition-colors group"
            >
              <span className="w-28 min-w-[7rem] text-xs font-medium truncate shrink-0 group-hover:text-[#284899]">{territory}</span>
              {Array.from({ length: 12 }, (_, i) => {
                const month = i + 1;
                const isPeak = data.mese_picco === month;
                const distance = Math.abs(month - data.mese_picco);
                const nearPeak = distance <= 1 || distance >= 11;
                const sr = data.sr ?? 0;

                let bg = "bg-transparent";
                if (isPeak) {
                  bg = sr > 4 ? "bg-red-500" : sr >= 2 ? "bg-amber-400" : "bg-emerald-400";
                } else if (nearPeak) {
                  bg = sr > 4 ? "bg-red-300/60" : sr >= 2 ? "bg-amber-300/50" : "bg-emerald-300/40";
                }

                return (
                  <span
                    key={month}
                    className={cn("w-8 min-w-[2rem] h-5 rounded-sm mx-px", bg)}
                    title={`${territory} - ${monthNames[i]}${isPeak ? " (picco)" : ""}`}
                  />
                );
              })}
            </button>
          ))}
          {/* Legend */}
          <div className="flex items-center gap-4 pt-3 mt-2 border-t border-border text-[10px] text-muted-foreground">
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-emerald-400" /> Bassa stagionalita</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-amber-400" /> Media</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-red-500" /> Alta stagionalita</span>
          </div>
        </div>
      ) : (
        <div className="space-y-1 max-h-[400px] overflow-y-auto">
          <div className="flex items-center gap-2 text-xs text-muted-foreground pb-2 border-b sticky top-0 bg-card">
            <span className="w-32 font-medium">Territorio</span>
            <span className="w-16 text-right">Presenze</span>
            <span className="w-12 text-right">SR</span>
            <span className="w-12 text-right">Picco</span>
          </div>
          {sorted.slice(0, 50).map(([territory, data]) => (
            <button
              key={territory}
              onClick={() => onSelect(territory)}
              className="flex items-center gap-2 w-full text-left px-1 py-1.5 rounded hover:bg-secondary/50 transition-colors text-sm"
            >
              <span className="w-32 truncate font-medium">{territory}</span>
              <span className="w-16 text-right text-muted-foreground">{formatNum(data.p_totale)}</span>
              <span className={cn(
                "w-12 text-right font-medium",
                data.sr !== null && data.sr > 4 ? "text-red-500" : data.sr !== null && data.sr >= 2 ? "text-muted-foreground" : "text-emerald-500"
              )}>
                {data.sr !== null ? data.sr.toFixed(1) : "-"}
              </span>
              <span className="w-12 text-right text-muted-foreground">{monthNames[(data.mese_picco || 1) - 1]}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
